import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import DeleteModal from '@/components/CommonComponents/DeleteModal';
import StatCard from '@/components/CommonComponents/StatCard';
import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { formatDate } from '@/helper/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { surveyManagementService } from '@/services/surveyManagementService/surveyManagementService';
import { AppDispatch, RootState } from '@/store';
import {
  deleteSurvey,
  fetchSurveysList,
  getSurveyQuestions,
  getSurveyStats,
  removeSurveyFromList,
  setFilters,
  setPage,
  setPageSize,
  updateSurveyInList,
  updateSurveyStatus,
} from '@/store/slices/surveyManagementSlice/surveyManagementSlice';
import { SurveyListItem } from '@/types/survey.types';
import { Eye, MessagesSquare, Paperclip } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Pagination from '../CommonComponents/Pagination';
import ServerSearchFilter from '../CommonComponents/ServerSearchFilter';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import SurveyPreviewModal from './SurveyPreviewModal';

export interface SurveyView extends SurveyListItem {
  project?: string;
  assignedSites?: string[];
  assignedSurveyset?: string[];
}

export default function SurveyManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const loading = useSelector((state: RootState) => state.survey.loading);
  const surveyStats = useSelector((state: RootState) => state.survey.surveyStats);
  const {
    data: surveysData,
    totalPages,
    page,
    limit,
    total,
  } = useSelector((state: RootState) => state.survey.list);

  const filters = useSelector((state: RootState) => state.survey.filters);

  const [filteredSurveys, setFilteredSurveys] = useState<SurveyListItem[]>(surveysData);

  const [selectedSurvey, setSelectedSurvey] = useState<SurveyListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSurvey, setPreviewSurvey] = useState<SurveyListItem | null>(null);

  // Get the updated survey data from Redux store for preview
  const getUpdatedPreviewSurvey = () => {
    if (!previewSurvey) return null;
    const updatedSurvey = surveysData.find((s) => s.id === previewSurvey.id);
    return updatedSurvey || previewSurvey;
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<SurveyListItem | null>(null);

  const { exportFile } = useExportFile();
  const debouncedFilters = useDebounce(filters, 500);

  const permissions = {
    create: usePermission('write', 'surveys'),
    export: usePermission('export', 'surveys'),
    edit: usePermission('edit', 'surveys'),
    delete: usePermission('delete', 'surveys'),
  };

  useEffect(() => {
    dispatch(getSurveyStats());
  }, [dispatch]);

  useEffect(() => {
    if (!surveysData || surveysData.length === 0) {
      setFilteredSurveys([]);
      return;
    }

    if (!filters?.search) {
      setFilteredSurveys(surveysData);
      return;
    }

    const searchText = filters.search.toLowerCase();

    const filtered = surveysData.filter((survey) => {
      if (String(survey.id).includes(searchText)) return true;

      if (survey.title?.toLowerCase().includes(searchText)) return true;

      return survey.surveySets?.some((item) => {
        const projectName = item?.surveySet?.site?.project?.name?.toLowerCase() || '';

        const siteName = item?.surveySet?.site?.name?.toLowerCase() || '';

        const surveySetTitle = item?.surveySet?.title?.toLowerCase() || '';

        return (
          projectName.includes(searchText) ||
          siteName.includes(searchText) ||
          surveySetTitle.includes(searchText)
        );
      });
    });

    setFilteredSurveys(filtered);
  }, [surveysData, filters]);

  useEffect(() => {
    let cancelled = false;

    const buildRows = async () => {
      if (!surveysData || surveysData.length === 0) {
        setFilteredSurveys([]);
        return;
      }

      const rows: SurveyListItem[] = await Promise.all(
        surveysData.map(async (survey) => {
          return {
            ...survey,
            _permissions: permissions,
          };
        }),
      );

      if (!cancelled) {
        setFilteredSurveys(rows);
      }
    };

    buildRows();

    return () => {
      cancelled = true;
    };
  }, [surveysData]);

  useEffect(() => {
    dispatch(
      fetchSurveysList({
        page,
        limit,
        search: debouncedFilters?.search,
        status: debouncedFilters?.status,
      }),
    );
  }, [dispatch, page, limit, debouncedFilters]);
  const renderProjects = (row: SurveyView) => {
    const projects = Array.from(
      new Set(row.surveySets?.map((item) => item?.surveySet?.site?.project?.name) || []),
    );

    if (!projects.length) {
      return <span className="inline-block bg-[#C9C9C9CC] px-6 py-1 rounded-full">N/A</span>;
    }

    const [first, ...rest] = projects;

    return (
      <div className="flex gap-2 justify-center">
        <span className="inline-block bg-[#C9C9C9CC] px-6 py-1 rounded-full">{first}</span>
        {rest.length > 0 && (
          <span className="inline-block bg-[#C9C9C9CC] px-2 py-1 rounded-full">+{rest.length}</span>
        )}
      </div>
    );
  };

  const openDeleteModal = (survey: SurveyListItem) => {
    const length = survey.surveySets.length;
    if (length > 0) {
      showToast.error(
        'Cannot delete the survey, this survey is attached to an "Active" Survey-set.',
      );
      return;
    }
    setSurveyToDelete(survey);
    setIsDeleteModalOpen(true);
  };

  const handleExport = () => {
    exportFile(() => surveyManagementService.exportSurveys(), `surveys_${Date.now()}.csv`);
  };

  const handlePreview = async (survey: SurveyListItem) => {
    setPreviewSurvey(survey);
    setIsPreviewOpen(true);

    // Fetch questions if not already loaded or if questionsJson is empty
    if (!survey.questionsJson || survey.questionsJson.length === 0) {
      try {
        await dispatch(getSurveyQuestions(String(survey.id)));
      } catch (error) {
        showToast.error('Failed to load survey questions');
      }
    }
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    let status: boolean | undefined;

    switch (newFilters?.status) {
      case 'Active':
        status = true;
        break;
      case 'Inactive':
        status = false;
        break;
      default:
        status = undefined;
    }

    const nextFilters = {
      search: newFilters?.search ?? '',
      status,
    };

    const filtersChanged =
      filters.search !== nextFilters.search || filters.status !== nextFilters.status;

    if (filtersChanged) {
      dispatch(setFilters(nextFilters));
    }
  };

  const handleStatusToggle = async (id: string, newStatus: boolean) => {
    const selected = surveysData.find((s) => Number(s.id) === Number(id));
    if (!selected) return;

    dispatch(
      updateSurveyInList({
        id: selected.id,
        updates: { status: newStatus },
      }),
    );

    try {
      await dispatch(
        updateSurveyStatus({
          id,
          data: { status: newStatus },
        }),
      ).unwrap();

      // showToast.success(`Survey status updated to ${newStatus ? 'Active' : 'Inactive'}`);

      dispatch(getSurveyStats());
    } catch (error: any) {
      showToast.error(error || 'Failed to update survey status');
      dispatch(
        updateSurveyInList({
          id: selected.id,
          updates: { status: !newStatus },
        }),
      );
    }
  };

  const handleEdit = (row: SurveyView) => {
    if (!row._permissions?.edit) return;
    const fixedRow = {
      ...row,
      defaultLanguageId: row.defaultLanguageId || 4,
    };
    navigate(`/dashboard/survey-management/edit`, {
      state: { from: 'survey-management', survey: fixedRow },
    });
  };

  const handleDelete = async () => {
    if (!surveyToDelete) return;

    dispatch(removeSurveyFromList(surveyToDelete.id));
    setIsDeleteModalOpen(false);
    setSurveyToDelete(null);

    try {
      const deletedBy = 4;

      await dispatch(
        deleteSurvey({
          id: String(surveyToDelete.id),
          data: deletedBy,
        }),
      ).unwrap();

      showToast.success('Survey deleted successfully!');

      dispatch(getSurveyStats());
    } catch (error: any) {
      showToast.error(error || 'Failed to delete survey');
      dispatch(fetchSurveysList({ page, limit: limit }));
    }
  };

  const surveyColumns: Column<SurveyView>[] = [
    { key: 'id', header: 'Survey ID' },

    { key: 'title', header: 'Survey Title' },
    {
      key: 'project',
      header: 'Linked Project',
      render: (_, row) => renderProjects(row),
    },
    {
      key: 'assignedSites',
      header: 'Assigned Site',
      render: (_, row) => {
        const uniqueSites = Array.from(
          new Map(
            (row.surveySets || []).map((item) => [
              item.surveySet.site.id,
              item.surveySet.site.name,
            ]),
          ).values(),
        );

        if (!uniqueSites.length) return <span className="items-center"> N/A</span>;

        const extraSitesCount = uniqueSites.length - 1;

        return (
          <div className="flex items-center justify-center gap-2">
            <span>{uniqueSites[0]}</span>
            {extraSitesCount > 0 && (
              <span className="inline-block bg-[#C9C9C9CC] text-[#1A0206] px-2 py-1 font-medium rounded-full font-lato">
                +{extraSitesCount}
              </span>
            )}
          </div>
        );
      },
    },

    {
      key: 'assignedSurveyset',
      header: 'Assigned Survey-Set',
      render: (_, row) => {
        const surveysets = row.surveySets?.slice(0, 1) || [];
        const extraSurveySetCount = row.surveySets.length - 1;

        if (!(surveysets.length > 0)) return <span className="items-center"> N/A</span>;

        return (
          <div className="flex gap-2 justify-center">
            {surveysets.map((item, index) => {
              const name = item?.surveySet?.title ?? '-';
              return (
                <div key={index} className="flex items-center gap-2">
                  <span>{name}</span>
                  {extraSurveySetCount > 0 && (
                    <span className="inline-block bg-[#C9C9C9CC] text-[#1A0206] px-2 py-1 font-medium rounded-full font-lato">
                      +{extraSurveySetCount}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },

    {
      key: 'createdAt',
      header: 'Created On',
      render: formatDate,
    },

    {
      key: 'status',
      header: 'Status',
    },
  ];

  const surveyStatsData = [
    {
      id: 'total',
      title: 'Total Surveys',
      value: surveyStats?.totalSurveys || 0,
      icon: Paperclip,
      iconBg: 'bg-[#E79C4CCC]',
    },
    {
      id: 'inactive',
      title: 'Inactive Surveys',
      value: surveyStats?.inactiveSurvey || 0,
      icon: MessagesSquare,
      iconBg: 'bg-[#F0DF4ACC]',
    },
    {
      id: 'active',
      title: 'Active Surveys',
      value: surveyStats?.activeSurveys || 0,
      icon: Eye,
      iconBg: 'bg-[#0BA96F80]',
    },
  ];

  return (
    <PageHeaderWrapper
      title="Survey Management"
      description="Manage and monitor surveys across all projects."
      onAdd={() =>
        navigate('/dashboard/survey-management/create', {
          state: { from: 'survey-management' },
        })
      }
      onExport={handleExport}
      addLabel="Add Survey"
      exportLabel="Export"
      permissions={permissions}
    >
      <ServerSearchFilter
        fields={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search items...',
          },
          {
            type: 'radio-select',
            key: 'status',
            label: 'Select',
            options: ['Active', 'Inactive'],
          },
        ]}
        onChange={handleFilterChange}
      />

      <div className="gap-4 justify-end grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 pt-2">
        {surveyStatsData.map((stat) => (
          <StatCard
            key={stat.id}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            iconSize={45}
            iconStroke={0.8}
            iconBg={stat.iconBg}
          />
        ))}
      </div>

      <div>
        <DataTable<SurveyView>
          listTitle=""
          columns={surveyColumns}
          data={filteredSurveys}
          loading={loading}
          onView={(row) => {
            setSelectedSurvey(row);
            setIsModalOpen(true);
          }}
          onEdit={handleEdit}
          onPreview={handlePreview}
          onDelete={(row) => {
            if (!row._permissions?.delete) return;
            openDeleteModal(row);
          }}
          onStatusToggle={handleStatusToggle}
          permissions={permissions}
        />

        <Pagination
          currentPage={page!}
          totalPages={totalPages ?? 0}
          pageSize={limit!}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
          onPageSizeChange={(newLimit) => dispatch(setPageSize(newLimit))}
        />
      </div>

      {isModalOpen && selectedSurvey && (
        <ViewDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Survey Details"
          data={selectedSurvey}
          fields={[
            { label: 'Survey ID', value: selectedSurvey.id },
            { label: 'Survey Title', value: selectedSurvey.title },
            {
              label: 'Linked Project',
              value:
                selectedSurvey.surveySets.length > 0
                  ? selectedSurvey.surveySets?.map((s) => s?.surveySet?.site?.project?.name)
                  : ['N/A'],
              list: true,
            },
            {
              label: 'Assigned Sites',
              value:
                selectedSurvey.surveySets.length > 0
                  ? selectedSurvey.surveySets.map((s) => s.surveySet.site.name)
                  : ['N/A'],
              list: true,
            },
            {
              label: 'Status',
              value: selectedSurvey.status ? 'Active' : 'Inactive',
              list: true,
            },
            {
              label: 'ODK File Name',
              value: selectedSurvey.odkFileUrl
                ? selectedSurvey.odkFileUrl.split('/').pop()?.split('_').slice(1).join('_') ||
                  'Unknown'
                : 'No file',
              list: true,
            },
          ]}
        />
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSurveyToDelete(null);
        }}
        onDelete={handleDelete}
        pageTitle={'Survey'}
        itemName={surveyToDelete?.title}
      />

      {/* Preview Popup */}

      <SurveyPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        getUpdatedPreviewSurvey={getUpdatedPreviewSurvey}
      />
    </PageHeaderWrapper>
  );
}
